import { TriangleAlert, LoaderCircle, CircleX } from 'lucide-react';
import type { ScanTaskState } from '@/lib/useScanTask';

interface ScanProgressProps {
  state: ScanTaskState;
  onCancel: () => void;
  onRetry?: () => void;
  featureLabel?: string;
  itemLabel?: string;
}

export function ScanProgress({ state, onCancel, onRetry, featureLabel = 'documents', itemLabel = 'Document' }: ScanProgressProps) {
  const { totalDocs, completedDocs, currentDocument, failedDocs, stoppedEarly, stoppedMessage, error } = state;
  const processed = completedDocs + failedDocs.length;
  const pct = totalDocs > 0 ? Math.round((processed / totalDocs) * 100) : 0;

  // Task failed or lost — show error with retry/cancel
  if (error) {
    return (
      <div className="max-w-lg mx-auto py-12 space-y-5">
        <div className="text-center space-y-1">
          <CircleX className="w-7 h-7 text-red-500 mx-auto" />
          <p className="text-sm font-medium text-slate-700">
            Something went wrong
          </p>
        </div>
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <TriangleAlert className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
        {totalDocs > 0 && (
          <p className="text-xs text-slate-400 text-center">
            Progress before failure: {completedDocs} of {totalDocs} {itemLabel.toLowerCase()}s completed.
          </p>
        )}
        <div className="flex items-center justify-center gap-3">
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-brand-orange text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors"
            >
              Retry
            </button>
          )}
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto py-12 space-y-5">
      {/* Header */}
      <div className="text-center space-y-1">
        <LoaderCircle className="w-7 h-7 text-brand-orange animate-spin mx-auto" />
        <p className="text-sm font-medium text-slate-700">
          Scanning {featureLabel}...
        </p>
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>{totalDocs > 0 ? `${itemLabel} ${Math.min(processed + 1, totalDocs)} of ${totalDocs}` : 'Starting...'}</span>
          <span>{pct}%</span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-orange rounded-full transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        {currentDocument && (
          <p className="text-xs text-slate-400 truncate">
            {currentDocument}
          </p>
        )}
      </div>

      {/* Stopped-early banner */}
      {stoppedEarly && stoppedMessage && (
        <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <TriangleAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{stoppedMessage}</p>
        </div>
      )}

      {/* Error log */}
      {failedDocs.length > 0 && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg space-y-1.5 max-h-40 overflow-y-auto">
          <p className="text-xs font-semibold text-red-700 uppercase tracking-wider flex items-center gap-1">
            <CircleX className="w-3.5 h-3.5" />
            {failedDocs.length} error{failedDocs.length !== 1 ? 's' : ''}
          </p>
          {failedDocs.map((err, i) => (
            <div key={i} className="text-xs text-red-700">
              <span className="font-medium">{err.document}:</span>{' '}
              <span className="text-red-600">{err.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Cancel */}
      <div className="text-center">
        <button
          onClick={onCancel}
          className="text-xs text-slate-500 hover:text-slate-700 underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
